import { useState, useEffect } from 'react'
import siteContent from '../../content/siteContent'
import './PrivacyPolicy.css'

export default function PrivacyPolicy() {
  // NEW: State to track selected language (Defaults to English)
  const [language, setLanguage] = useState('EN');
  
  // Dynamically select the content object based on the language state
  const content = siteContent.privacyPolicy[language];
  
  // Start with the first section ID from the selected language content
  const [activeSection, setActiveSection] = useState(content.contents[0].id)

  // Force scroll to top on mount or when language changes
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [language])

  // Intersection Observer for scroll-spy functionality
  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '-45% 0px -45% 0px',
      threshold: 0
    }

    const observerCallback = (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && window.scrollY > 50) {
          setActiveSection(entry.target.id)
        } 
        if (window.scrollY < 100) {
          setActiveSection(content.contents[0].id)
        }
      })
    }

    const observer = new IntersectionObserver(observerCallback, observerOptions)
    const sectionElements = document.querySelectorAll('.privacy-section')
    
    sectionElements.forEach((element) => observer.observe(element))

    return () => {
      sectionElements.forEach((element) => observer.unobserve(element))
      observer.disconnect()
    }
  }, [content.contents])

  const handleContentsClick = (e, sectionId) => {
    e.preventDefault()
    const sectionElement = document.getElementById(sectionId)
    
    if (sectionElement) {
      const offset = 100
      const bodyRect = document.body.getBoundingClientRect().top
      const elementRect = sectionElement.getBoundingClientRect().top
      const elementPosition = elementRect - bodyRect
      const offsetPosition = elementPosition - offset

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      })
      
      setActiveSection(sectionId)
    }
  }

  return (
    <div className="privacy-page">
      {/* Header Section */}
      <section className="privacy-header-section">
        <div className="container">
          <div className="privacy-badge">{content.badge}</div>

          {/* NEW: Language Toggle UI */}
          <div className="language-toggle-container">
            <div className="language-toggle">
              <button 
                className={`lang-btn ${language === 'EN' ? 'active' : ''}`}
                onClick={() => setLanguage('EN')}
              >
                EN
              </button>
              <span className="lang-divider">|</span>
              <button 
                className={`lang-btn ${language === 'HI' ? 'active' : ''}`}
                onClick={() => setLanguage('HI')}
              >
                HI
              </button>
            </div>
          </div>

          <h1 className="privacy-title">{content.title}</h1>
          <p className="privacy-subtitle">{content.subtitle}</p>
          <p className="privacy-updated">{content.lastUpdated}</p>
        </div>
      </section>

      <div className="privacy-divider"></div>

      {/* Main Content */}
      <section className="privacy-content-section">
        <div className="container">
          <div className="privacy-grid">
            {/* Left Column - Contents Sidebar */}
            <aside className="privacy-sidebar">
              <div className="privacy-contents">
              <h3 className="contents-heading">{content.contentsTitle}</h3>
                <nav className="contents-nav">
                  <ul className="contents-list">
                    {content.contents.map((item) => (
                      <li key={item.id}>
                        <a 
                          href={`#${item.id}`}
                          className={`contents-link ${
                            activeSection === item.id ? 'active' : ''
                          }`}
                          onClick={(e) => handleContentsClick(e, item.id)}
                        >
                          {item.label}
                        </a>
                      </li>
                    ))}
                  </ul>
                </nav>
              </div>
            </aside>

            {/* Right Column - Main Document Body */}
            <main className="privacy-main">
              {content.sections.map((section) => (
                <article
                  key={section.id}
                  id={section.id}
                  className="privacy-section"
                >
                  <div className="section-number">{section.number}</div>
                  <h2 className="section-title">{section.title}</h2>
                  <div className="section-content">
                    {section.paragraphs.map((paragraph, idx) => (
                      <p key={idx} className="section-paragraph">
                        {paragraph}
                      </p>
                    ))}
                  </div>
                </article>
              ))}
            </main>
          </div>
        </div>
      </section>
    </div>
  )
}